import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Users, TrendingUp, MessageSquare, LogOut, ChevronDown,
  Phone, Mail, Trash2, Star, BarChart3, Eye, FolderOpen, Plus, X,
  Image, Video, Edit2, Save, XCircle, Settings, Layers
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import FileUploader from "@/components/admin/FileUploader";

const statusMap = {
  nuevo: { label: "Nuevo", color: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
  contactado: { label: "Contactado", color: "bg-amber-500/20 text-amber-400 border-amber-500/30" },
  convertido: { label: "Convertido", color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" },
  descartado: { label: "Descartado", color: "bg-neutral-500/20 text-neutral-400 border-neutral-500/30" },
};

function ProjectEditor({ project, token, onSaved, onCancel }) {
  const [form, setForm] = useState({
    title: project.title,
    category: project.category || "",
    description: project.description || "",
    cover_image: project.cover_image || "",
    gallery: project.gallery || [],
    real_photos: project.real_photos || [],
    videos: project.videos || [],
  });
  const [newUrl, setNewUrl] = useState({ gallery: "", real_photos: "", videos: "" });
  const [saving, setSaving] = useState(false);

  const addItem = (field) => {
    if (!newUrl[field].trim()) return;
    setForm({ ...form, [field]: [...form[field], newUrl[field].trim()] });
    setNewUrl({ ...newUrl, [field]: "" });
  };

  const removeItem = (field, index) => {
    setForm({ ...form, [field]: form[field].filter((_, i) => i !== index) });
  };

  const handleSave = async () => {
    if (!form.title || !form.cover_image) { toast.error("Título y portada requeridos"); return; }
    setSaving(true);
    try {
      await api.updateProject(project.id, form, token);
      toast.success("Proyecto actualizado");
      onSaved();
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  const renderMediaList = (field, label, icon) => (
    <div className="mt-4">
      <p className="text-neutral-500 text-xs tracking-widest uppercase mb-2 flex items-center gap-1">{icon} {label} ({form[field].length})</p>
      <div className="space-y-2">
        {form[field].map((url, i) => (
          <div key={i} className="flex items-center gap-2 bg-neutral-800/50 rounded-lg p-2">
            {field === "videos" ? (
              <video src={url} className="w-16 h-12 object-cover rounded" />
            ) : (
              <img src={url} alt="" className="w-16 h-12 object-cover rounded" />
            )}
            <p className="text-neutral-400 text-xs truncate flex-1">{url.split('/').pop()}</p>
            <button onClick={() => removeItem(field, i)} className="text-red-400/60 hover:text-red-400 transition-colors flex-shrink-0" data-testid={`remove-${field}-${i}`}>
              <XCircle size={16} />
            </button>
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-2">
        <input
          value={newUrl[field]}
          onChange={(e) => setNewUrl({ ...newUrl, [field]: e.target.value })}
          placeholder="URL o sube un archivo..."
          className="flex-1 bg-transparent border border-neutral-800 focus:border-neutral-600 rounded-lg text-white px-3 py-2 text-xs outline-none placeholder:text-neutral-700"
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addItem(field))}
        />
        <button onClick={() => addItem(field)} className="px-3 py-2 bg-neutral-800 hover:bg-neutral-700 rounded-lg text-xs text-white transition-colors" data-testid={`add-${field}-btn`}>
          <Plus size={14} />
        </button>
        <FileUploader token={token} label={field === "videos" ? "Subir video" : "Subir foto"} accept={field === "videos" ? "video/*" : "image/*"} onUploaded={(url) => { setForm({ ...form, [field]: [...form[field], url] }); }} />
      </div>
    </div>
  );

  const inputClass = "w-full bg-transparent border-b border-neutral-800 focus:border-white text-white pb-2 pt-1 text-sm outline-none transition-colors placeholder:text-neutral-600";

  return (
    <div className="bg-neutral-900/50 border border-neutral-800 rounded-2xl p-6 space-y-4" data-testid="project-editor">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-white font-medium" style={{ fontFamily: "Outfit, sans-serif" }}>Editando: {project.title}</h3>
        <button onClick={onCancel} className="text-neutral-500 hover:text-white transition-colors"><X size={18} /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-neutral-500 text-xs tracking-widest uppercase mb-1 block">Título</label>
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputClass} data-testid="edit-title" />
        </div>
        <div>
          <label className="text-neutral-500 text-xs tracking-widest uppercase mb-1 block">Categoría</label>
          <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputClass} />
        </div>
      </div>
      <div>
        <label className="text-neutral-500 text-xs tracking-widest uppercase mb-1 block">Descripción</label>
        <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputClass} />
      </div>
      <div>
        <label className="text-neutral-500 text-xs tracking-widest uppercase mb-1 block">Portada</label>
        <div className="flex items-center gap-3">
          <input value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} className={`${inputClass} flex-1`} placeholder="URL o sube un archivo" />
          <FileUploader token={token} label="Subir" accept="image/*" onUploaded={(url) => setForm({ ...form, cover_image: url })} />
          {form.cover_image && <img src={form.cover_image} alt="cover" className="w-16 h-12 object-cover rounded" />}
        </div>
      </div>

      {renderMediaList("gallery", "Galería / Renders", <Image size={12} />)}
      {renderMediaList("real_photos", "Fotos Reales", <Image size={12} />)}
      {renderMediaList("videos", "Videos", <Video size={12} />)}

      <div className="flex gap-3 pt-4 border-t border-neutral-800">
        <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 bg-white text-black px-5 py-2.5 rounded-full text-sm font-medium hover:bg-neutral-200 transition-all disabled:opacity-50" data-testid="save-project-btn">
          <Save size={14} /> {saving ? "Guardando..." : "Guardar cambios"}
        </button>
        <button onClick={onCancel} className="px-5 py-2.5 rounded-full text-sm text-neutral-400 border border-neutral-800 hover:text-white hover:border-neutral-600 transition-all">Cancelar</button>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total_leads: 0, new_leads: 0, contacted_leads: 0, converted_leads: 0, total_projects: 0 });
  const [conversations, setConversations] = useState([]);
  const [projects, setProjects] = useState([]);
  const [activeTab, setActiveTab] = useState("leads");
  const [selectedConv, setSelectedConv] = useState(null);
  const [convMessages, setConvMessages] = useState([]);
  const [showNewProject, setShowNewProject] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [newProject, setNewProject] = useState({ title: "", category: "", description: "", cover_image: "" });
  const [clients, setClients] = useState([]);
  const [newClient, setNewClient] = useState({ name: "", logo_url: "" });
  const [showNewClient, setShowNewClient] = useState(false);
  const [siteSettings, setSiteSettings] = useState({ phone: "", email: "", address: "", instagram: "", facebook: "", website: "", whatsapp: "" });
  const [carouselSlides, setCarouselSlides] = useState([]);
  const [newSlide, setNewSlide] = useState({ title: "", subtitle: "", image_url: "" });
  const [showNewSlide, setShowNewSlide] = useState(false);
  const [editingSlide, setEditingSlide] = useState(null);
  const navigate = useNavigate();
  const token = localStorage.getItem("admin_token");

  const loadData = useCallback(async () => {
    if (!token) { navigate("/admin/login"); return; }
    const safeCall = async (fn) => { try { return await fn(); } catch (err) { if (err?.response?.status === 401) throw err; return null; } };
    try {
      const [leadsRes, statsRes, convsRes, projRes, clientsRes, settingsRes, carouselRes] = await Promise.all([
        safeCall(() => api.getLeads(token)),
        safeCall(() => api.getStats(token)),
        safeCall(() => api.getConversations(token)),
        safeCall(() => api.getProjects()),
        safeCall(() => api.getClients()),
        safeCall(() => api.getSettings()),
        safeCall(() => api.getAdminCarousel(token)),
      ]);
      if (leadsRes) setLeads(leadsRes.data);
      if (statsRes) setStats(statsRes.data);
      if (convsRes) setConversations(convsRes.data);
      if (projRes) setProjects(projRes.data);
      if (clientsRes) setClients(clientsRes.data);
      if (settingsRes?.data) setSiteSettings(settingsRes.data);
      if (carouselRes) setCarouselSlides(carouselRes.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Sesión expirada");
        localStorage.removeItem("admin_token");
        localStorage.removeItem("admin_user");
        navigate("/admin/login");
      }
    }
  }, [token, navigate]);

  useEffect(() => { loadData(); }, [loadData]);

  const updateStatus = async (id, status) => {
    try { await api.updateLead(id, { status }, token); toast.success("Actualizado"); loadData(); } catch { toast.error("Error"); }
  };
  const deleteLead = async (id) => {
    try { await api.deleteLead(id, token); toast.success("Eliminado"); loadData(); } catch { toast.error("Error"); }
  };
  const deleteProject = async (id) => {
    if (!window.confirm("¿Eliminar este proyecto?")) return;
    try { await api.deleteProject(id, token); toast.success("Proyecto eliminado"); loadData(); } catch { toast.error("Error"); }
  };
  const createProject = async (e) => {
    e.preventDefault();
    if (!newProject.title || !newProject.cover_image) { toast.error("Título y portada requeridos"); return; }
    try {
      await api.createProject({ ...newProject, gallery: [], real_photos: [], videos: [] }, token);
      toast.success("Proyecto creado — ahora puedes editarlo para agregar imágenes y videos");
      setShowNewProject(false); setNewProject({ title: "", category: "", description: "", cover_image: "" }); loadData();
    } catch { toast.error("Error al crear"); }
  };
  const viewConversation = async (sessionId) => {
    try { const res = await api.getConversationMessages(sessionId, token); setConvMessages(res.data); setSelectedConv(sessionId); } catch { toast.error("Error"); }
  };
  const logout = () => { localStorage.removeItem("admin_token"); localStorage.removeItem("admin_user"); navigate("/admin/login"); };

  const addClient = async () => {
    if (!newClient.name || !newClient.logo_url) { toast.error("Nombre y URL del logo son requeridos"); return; }
    try { await api.createClient(newClient, token); toast.success("Cliente agregado"); setNewClient({ name: "", logo_url: "" }); setShowNewClient(false); loadData(); } catch { toast.error("Error al agregar"); }
  };
  const removeClient = async (id) => {
    try { await api.deleteClient(id, token); toast.success("Cliente eliminado"); loadData(); } catch { toast.error("Error al eliminar"); }
  };
  const saveSettings = async () => {
    try { await api.updateSettings(siteSettings, token); toast.success("Configuración guardada"); } catch { toast.error("Error al guardar"); }
  };

  const addSlide = async () => {
    if (!newSlide.title || !newSlide.image_url) { toast.error("Título e imagen requeridos"); return; }
    try { await api.createCarouselSlide(newSlide, token); toast.success("Slide agregado"); setNewSlide({ title: "", subtitle: "", image_url: "" }); setShowNewSlide(false); loadData(); } catch { toast.error("Error al agregar"); }
  };
  const removeSlide = async (id) => {
    if (!window.confirm("¿Eliminar este slide?")) return;
    try { await api.deleteCarouselSlide(id, token); toast.success("Slide eliminado"); loadData(); } catch { toast.error("Error"); }
  };
  const saveSlide = async (id, data) => {
    try { await api.updateCarouselSlide(id, data, token); toast.success("Slide actualizado"); setEditingSlide(null); loadData(); } catch { toast.error("Error al actualizar"); }
  };
  const toggleSlide = async (slide) => {
    try { await api.updateCarouselSlide(slide.id, { active: !slide.active }, token); loadData(); } catch { toast.error("Error"); }
  };

  const statCards = [
    { label: "Total Leads", value: stats.total_leads, icon: Users, accent: "text-white" },
    { label: "Nuevos", value: stats.new_leads, icon: Star, accent: "text-blue-400" },
    { label: "Contactados", value: stats.contacted_leads, icon: Phone, accent: "text-amber-400" },
    { label: "Proyectos", value: stats.total_projects || projects.length, icon: FolderOpen, accent: "text-emerald-400" },
  ];

  const inputClass = "w-full bg-transparent border-b border-neutral-800 focus:border-white text-white pb-2 pt-1 text-sm outline-none transition-colors placeholder:text-neutral-600";

  return (
    <div className="dark min-h-screen bg-[#0A0A0A] text-white" data-testid="admin-dashboard">
      <header className="border-b border-neutral-800 px-6 md:px-10 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>MERZA</h1>
          <span className="text-neutral-600 text-sm">|</span>
          <span className="text-neutral-500 text-xs tracking-widest uppercase">CRM</span>
        </div>
        <button data-testid="admin-logout-button" onClick={logout} className="flex items-center gap-2 text-neutral-500 hover:text-white transition-colors text-sm">
          <LogOut size={16} strokeWidth={1.5} /> Salir
        </button>
      </header>

      <div className="px-6 md:px-10 py-8 max-w-[1600px] mx-auto">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {statCards.map((s) => (
            <div key={s.label} className="bg-neutral-900/50 border border-neutral-800 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-neutral-500 text-xs tracking-widest uppercase">{s.label}</span>
                <s.icon size={16} className={s.accent} strokeWidth={1.5} />
              </div>
              <p className="text-3xl font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-neutral-900/50 p-1 rounded-full w-fit border border-neutral-800">
          {[
            { id: "leads", label: "Leads", icon: Users },
            { id: "projects", label: "Proyectos", icon: FolderOpen },
            { id: "carousel", label: "Carrusel", icon: Layers },
            { id: "clients", label: "Clientes", icon: Image },
            { id: "conversations", label: "Chat", icon: MessageSquare },
            { id: "settings", label: "Config", icon: Settings },
          ].map((tab) => (
            <button key={tab.id} data-testid={`tab-${tab.id}`} onClick={() => { setActiveTab(tab.id); setSelectedConv(null); setEditingProject(null); }}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm transition-all ${activeTab === tab.id ? "bg-white text-black" : "text-neutral-500 hover:text-white"}`}>
              <tab.icon size={14} strokeWidth={1.5} /> {tab.label}
            </button>
          ))}
        </div>

        {/* LEADS TAB */}
        {activeTab === "leads" && (
          <div className="bg-neutral-900/30 border border-neutral-800 rounded-2xl overflow-hidden">
            <ScrollArea className="w-full">
              <Table>
                <TableHeader>
                  <TableRow className="border-neutral-800 hover:bg-transparent">
                    <TableHead className="text-neutral-500 text-xs tracking-widest uppercase">Nombre</TableHead>
                    <TableHead className="text-neutral-500 text-xs tracking-widest uppercase">Contacto</TableHead>
                    <TableHead className="text-neutral-500 text-xs tracking-widest uppercase">Servicio</TableHead>
                    <TableHead className="text-neutral-500 text-xs tracking-widest uppercase">Estado</TableHead>
                    <TableHead className="text-neutral-500 text-xs tracking-widest uppercase">Fecha</TableHead>
                    <TableHead className="text-neutral-500 text-xs tracking-widest uppercase text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leads.length === 0 ? (
                    <TableRow className="border-neutral-800"><TableCell colSpan={6} className="text-center text-neutral-600 py-12">No hay leads aún</TableCell></TableRow>
                  ) : leads.map((lead) => (
                    <TableRow key={lead.id} className="border-neutral-800 hover:bg-neutral-800/30">
                      <TableCell className="text-white font-medium">{lead.name}</TableCell>
                      <TableCell>
                        <span className="text-neutral-400 text-sm flex items-center gap-1"><Mail size={12} /> {lead.email}</span>
                        {lead.phone && <span className="text-neutral-500 text-xs flex items-center gap-1 mt-1"><Phone size={12} /> {lead.phone}</span>}
                      </TableCell>
                      <TableCell className="text-neutral-400 text-sm">{lead.service || "—"}</TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs border ${statusMap[lead.status]?.color || statusMap.nuevo.color}`}>
                              {statusMap[lead.status]?.label || lead.status} <ChevronDown size={12} />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="bg-neutral-900 border-neutral-800">
                            {Object.entries(statusMap).map(([key, val]) => (
                              <DropdownMenuItem key={key} onClick={() => updateStatus(lead.id, key)} className="text-neutral-300 hover:text-white focus:text-white focus:bg-neutral-800">{val.label}</DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                      <TableCell className="text-neutral-500 text-sm">{new Date(lead.created_at).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' })}</TableCell>
                      <TableCell className="text-right">
                        <button onClick={() => deleteLead(lead.id)} className="text-neutral-600 hover:text-red-400 transition-colors"><Trash2 size={16} /></button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          </div>
        )}

        {/* PROJECTS TAB */}
        {activeTab === "projects" && (
          <div data-testid="projects-management">
            {/* Editing a project */}
            {editingProject && (
              <div className="mb-6">
                <ProjectEditor project={editingProject} token={token} onSaved={() => { setEditingProject(null); loadData(); }} onCancel={() => setEditingProject(null)} />
              </div>
            )}

            {!editingProject && (
              <>
                <div className="flex items-center justify-between mb-6">
                  <p className="text-neutral-500 text-sm">{projects.length} proyectos</p>
                  <button onClick={() => setShowNewProject(!showNewProject)} className="flex items-center gap-2 bg-white text-black px-4 py-2 rounded-full text-sm font-medium hover:bg-neutral-200 transition-all" data-testid="add-project-button">
                    {showNewProject ? <><X size={14} /> Cancelar</> : <><Plus size={14} /> Nuevo proyecto</>}
                  </button>
                </div>

                {showNewProject && (
                  <form onSubmit={createProject} className="bg-neutral-900/50 border border-neutral-800 rounded-2xl p-6 mb-6 space-y-4" data-testid="new-project-form">
                    <p className="text-neutral-400 text-xs mb-2">Paso 1: Crea el proyecto. Paso 2: Edítalo para agregar imágenes y videos.</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-neutral-500 text-xs tracking-widest uppercase mb-1 block">Título *</label>
                        <input data-testid="project-title-input" value={newProject.title} onChange={(e) => setNewProject({ ...newProject, title: e.target.value })} placeholder="Nombre del proyecto" className={inputClass} />
                      </div>
                      <div>
                        <label className="text-neutral-500 text-xs tracking-widest uppercase mb-1 block">Categoría</label>
                        <input value={newProject.category} onChange={(e) => setNewProject({ ...newProject, category: e.target.value })} placeholder="Residencial, Comercial..." className={inputClass} />
                      </div>
                    </div>
                    <div>
                      <label className="text-neutral-500 text-xs tracking-widest uppercase mb-1 block">Descripción</label>
                      <input value={newProject.description} onChange={(e) => setNewProject({ ...newProject, description: e.target.value })} placeholder="Descripción breve..." className={inputClass} />
                    </div>
                    <div>
                      <label className="text-neutral-500 text-xs tracking-widest uppercase mb-1 block">Imagen de portada *</label>
                      <div className="flex items-center gap-3">
                        <input data-testid="project-cover-input" value={newProject.cover_image} onChange={(e) => setNewProject({ ...newProject, cover_image: e.target.value })} placeholder="URL o sube un archivo" className={`${inputClass} flex-1`} />
                        <FileUploader token={token} label="Subir foto" accept="image/*" onUploaded={(url) => setNewProject({ ...newProject, cover_image: url })} />
                      </div>
                      {newProject.cover_image && <img src={newProject.cover_image} alt="Preview" className="h-24 w-full object-cover rounded-xl mt-2 border border-neutral-800" />}
                    </div>
                    <button data-testid="create-project-submit" type="submit" className="bg-white text-black px-6 py-3 rounded-full text-sm font-medium hover:bg-neutral-200 transition-all">Crear proyecto</button>
                  </form>
                )}

                {/* Projects grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {projects.map((p) => (
                    <div key={p.id} className="bg-neutral-900/30 border border-neutral-800 rounded-2xl overflow-hidden group" data-testid={`admin-project-${p.id}`}>
                      <div className="aspect-[16/10] overflow-hidden relative">
                        <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        <div className="absolute top-3 right-3 flex gap-2">
                          <button onClick={() => setEditingProject(p)} className="w-8 h-8 rounded-full bg-white/80 backdrop-blur text-black flex items-center justify-center hover:bg-white transition-colors" data-testid={`edit-project-${p.id}`}>
                            <Edit2 size={14} />
                          </button>
                          <button onClick={() => deleteProject(p.id)} className="w-8 h-8 rounded-full bg-red-500/80 backdrop-blur text-white flex items-center justify-center hover:bg-red-500 transition-colors" data-testid={`delete-project-${p.id}`}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                      <div className="p-4">
                        <p className="text-neutral-500 text-[10px] tracking-widest uppercase">{p.category}</p>
                        <h3 className="text-white font-medium tracking-tight mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>{p.title}</h3>
                        <div className="flex items-center gap-3 mt-2 text-neutral-600 text-xs">
                          {(p.gallery?.length || 0) > 0 && <span className="flex items-center gap-1"><Image size={10} /> {p.gallery.length} renders</span>}
                          {(p.real_photos?.length || 0) > 0 && <span className="flex items-center gap-1"><Image size={10} /> {p.real_photos.length} reales</span>}
                          {(p.videos?.length || 0) > 0 && <span className="flex items-center gap-1"><Video size={10} /> {p.videos.length}</span>}
                        </div>
                        <button onClick={() => setEditingProject(p)} className="mt-3 text-xs text-neutral-500 hover:text-white transition-colors flex items-center gap-1">
                          <Edit2 size={11} /> Editar contenido
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* CONVERSATIONS TAB */}
        {activeTab === "conversations" && !selectedConv && (
          <div className="grid gap-3">
            {conversations.length === 0 ? (
              <div className="bg-neutral-900/30 border border-neutral-800 rounded-2xl p-12 text-center text-neutral-600">No hay conversaciones aún</div>
            ) : conversations.map((conv) => (
              <button key={conv.session_id} onClick={() => viewConversation(conv.session_id)}
                className="bg-neutral-900/30 border border-neutral-800 rounded-2xl p-5 flex items-center justify-between hover:bg-neutral-800/30 transition-colors text-left w-full">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-neutral-800 flex items-center justify-center"><MessageSquare size={16} className="text-neutral-400" /></div>
                  <div>
                    <p className="text-white text-sm font-medium">Sesión {conv.session_id.slice(0, 8)}...</p>
                    <p className="text-neutral-500 text-xs">{conv.message_count} mensajes</p>
                  </div>
                </div>
                <Eye size={16} className="text-neutral-600" />
              </button>
            ))}
          </div>
        )}
        {activeTab === "conversations" && selectedConv && (
          <div>
            <button onClick={() => setSelectedConv(null)} className="text-neutral-500 hover:text-white text-sm mb-4">← Volver</button>
            <div className="bg-neutral-900/30 border border-neutral-800 rounded-2xl p-6 space-y-4">
              {convMessages.map((msg, i) => (
                <div key={i} className="space-y-2">
                  <div className="flex gap-3">
                    <div className="w-7 h-7 rounded-full bg-neutral-700 flex items-center justify-center flex-shrink-0 mt-0.5"><Users size={12} className="text-neutral-400" /></div>
                    <p className="text-neutral-300 text-sm bg-neutral-800/50 rounded-2xl rounded-tl-sm px-4 py-3">{msg.user_message}</p>
                  </div>
                  <div className="flex gap-3 justify-end">
                    <p className="text-white text-sm bg-white/5 rounded-2xl rounded-tr-sm px-4 py-3 border border-neutral-800">{msg.bot_response}</p>
                    <div className="w-7 h-7 rounded-full bg-white flex items-center justify-center flex-shrink-0 mt-0.5"><BarChart3 size={12} className="text-black" /></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ===== CLIENTS TAB ===== */}
        {activeTab === "clients" && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>Clientes ({clients.length})</h3>
              <button data-testid="new-client-btn" onClick={() => setShowNewClient(!showNewClient)}
                className="flex items-center gap-2 px-5 py-2.5 bg-white text-black text-sm font-medium rounded-full hover:bg-neutral-200 transition-colors">
                {showNewClient ? <X size={14} /> : <Plus size={14} />} {showNewClient ? "Cancelar" : "Nuevo cliente"}
              </button>
            </div>

            {showNewClient && (
              <div className="bg-neutral-900/30 border border-neutral-800 rounded-2xl p-6 space-y-4">
                <input data-testid="client-name-input" className={inputClass} placeholder="Nombre del cliente" value={newClient.name} onChange={(e) => setNewClient({ ...newClient, name: e.target.value })} />
                <input data-testid="client-logo-input" className={inputClass} placeholder="URL del logo (imagen)" value={newClient.logo_url} onChange={(e) => setNewClient({ ...newClient, logo_url: e.target.value })} />
                {newClient.logo_url && (
                  <div className="w-20 h-20 rounded-xl border border-neutral-800 bg-neutral-900 p-3 flex items-center justify-center">
                    <img src={newClient.logo_url} alt="Preview" className="max-w-full max-h-full object-contain" />
                  </div>
                )}
                <button data-testid="save-client-btn" onClick={addClient} className="flex items-center gap-2 px-5 py-2.5 bg-white text-black text-sm font-medium rounded-full hover:bg-neutral-200 transition-colors">
                  <Save size={14} /> Guardar cliente
                </button>
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {clients.map((c) => (
                <div key={c.id} data-testid={`client-card-${c.id}`} className="group bg-neutral-900/30 border border-neutral-800 rounded-2xl p-5 flex flex-col items-center gap-3 relative hover:border-neutral-700 transition-colors">
                  <button data-testid={`delete-client-${c.id}`} onClick={() => removeClient(c.id)}
                    className="absolute top-3 right-3 w-7 h-7 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center text-red-400 hover:bg-red-500/20 transition-colors opacity-0 group-hover:opacity-100">
                    <Trash2 size={12} />
                  </button>
                  <div className="w-20 h-20 rounded-xl bg-white/5 border border-white/[0.06] p-3 flex items-center justify-center">
                    <img src={c.logo_url} alt={c.name} className="max-w-full max-h-full object-contain" />
                  </div>
                  <p className="text-xs text-neutral-400 tracking-wider uppercase text-center">{c.name}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ===== CAROUSEL TAB ===== */}
        {activeTab === "carousel" && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>Carrusel de Inicio</h3>
              <button data-testid="add-slide-btn" onClick={() => setShowNewSlide(true)}
                className="flex items-center gap-2 px-5 py-2.5 bg-white text-black text-sm font-medium rounded-full hover:bg-neutral-200 transition-colors">
                <Plus size={14} /> Nuevo Slide
              </button>
            </div>

            {showNewSlide && (
              <div className="bg-neutral-900/50 border border-neutral-800 rounded-2xl p-6 space-y-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-white font-medium">Agregar Slide</p>
                  <button onClick={() => setShowNewSlide(false)} className="text-neutral-500 hover:text-white"><X size={16} /></button>
                </div>
                <input data-testid="new-slide-title" className={inputClass} placeholder="Título (ej: Edificios Corporativos)" value={newSlide.title} onChange={e => setNewSlide({ ...newSlide, title: e.target.value })} />
                <input data-testid="new-slide-subtitle" className={inputClass} placeholder="Subtítulo descriptivo" value={newSlide.subtitle} onChange={e => setNewSlide({ ...newSlide, subtitle: e.target.value })} />
                <div>
                  <label className="text-neutral-500 text-xs tracking-widest uppercase mb-1 block">Imagen</label>
                  <div className="flex items-center gap-3">
                    <input data-testid="new-slide-image" className={`${inputClass} flex-1`} placeholder="URL o sube un archivo" value={newSlide.image_url} onChange={e => setNewSlide({ ...newSlide, image_url: e.target.value })} />
                    <FileUploader token={token} label="Subir foto" accept="image/*" onUploaded={(url) => setNewSlide({ ...newSlide, image_url: url })} />
                  </div>
                </div>
                {newSlide.image_url && <img src={newSlide.image_url} alt="Preview" className="h-32 w-full object-cover rounded-xl border border-neutral-800" onError={e => { e.target.style.display = 'none'; }} />}
                <button data-testid="save-new-slide" onClick={addSlide}
                  className="flex items-center gap-2 px-5 py-2.5 bg-white text-black text-sm font-medium rounded-full hover:bg-neutral-200 transition-colors">
                  <Save size={14} /> Guardar
                </button>
              </div>
            )}

            <div className="space-y-3">
              {carouselSlides.map((slide, i) => (
                <div key={slide.id} data-testid={`carousel-slide-${slide.id}`}
                  className={`group bg-neutral-900/30 border rounded-2xl overflow-hidden transition-colors ${slide.active ? 'border-neutral-800 hover:border-neutral-700' : 'border-neutral-800/50 opacity-50'}`}>
                  {editingSlide === slide.id ? (
                    <div className="p-5 space-y-3">
                      <input className={inputClass} defaultValue={slide.title} id={`edit-title-${slide.id}`} placeholder="Título" />
                      <input className={inputClass} defaultValue={slide.subtitle} id={`edit-subtitle-${slide.id}`} placeholder="Subtítulo" />
                      <input className={inputClass} defaultValue={slide.image_url} id={`edit-image-${slide.id}`} placeholder="URL de imagen" />
                      <div className="flex gap-2">
                        <button onClick={() => {
                          const t = document.getElementById(`edit-title-${slide.id}`).value;
                          const s = document.getElementById(`edit-subtitle-${slide.id}`).value;
                          const img = document.getElementById(`edit-image-${slide.id}`).value;
                          saveSlide(slide.id, { title: t, subtitle: s, image_url: img });
                        }} className="flex items-center gap-2 px-4 py-2 bg-white text-black text-xs font-medium rounded-full hover:bg-neutral-200 transition-colors">
                          <Save size={12} /> Guardar
                        </button>
                        <button onClick={() => setEditingSlide(null)} className="px-4 py-2 text-neutral-500 text-xs hover:text-white transition-colors">Cancelar</button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-4 p-3 pr-5">
                      <div className="w-32 h-20 rounded-xl overflow-hidden flex-shrink-0 bg-neutral-800">
                        <img src={slide.image_url} alt={slide.title} className="w-full h-full object-cover" onError={e => { e.target.src = 'https://images.unsplash.com/photo-1487958449943-2429e8be8625?w=400&q=60'; }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">{slide.title}</p>
                        <p className="text-neutral-500 text-xs truncate mt-1">{slide.subtitle}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-neutral-600 text-xs font-mono">{String(i + 1).padStart(2, '0')}</span>
                        <button data-testid={`toggle-slide-${slide.id}`} onClick={() => toggleSlide(slide)}
                          className={`w-10 h-5 rounded-full transition-colors relative ${slide.active ? 'bg-emerald-500/30 border border-emerald-500/50' : 'bg-neutral-800 border border-neutral-700'}`}>
                          <div className={`w-3.5 h-3.5 rounded-full transition-all absolute top-[2px] ${slide.active ? 'bg-emerald-400 left-[22px]' : 'bg-neutral-500 left-[2px]'}`} />
                        </button>
                        <button data-testid={`edit-slide-${slide.id}`} onClick={() => setEditingSlide(slide.id)}
                          className="w-8 h-8 rounded-full flex items-center justify-center text-neutral-500 hover:text-white hover:bg-white/5 transition-colors">
                          <Edit2 size={13} />
                        </button>
                        <button data-testid={`delete-slide-${slide.id}`} onClick={() => removeSlide(slide.id)}
                          className="w-8 h-8 rounded-full flex items-center justify-center text-neutral-500 hover:text-red-400 hover:bg-red-500/10 transition-colors opacity-0 group-hover:opacity-100">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {carouselSlides.length === 0 && (
                <div className="text-center py-16 text-neutral-600 text-sm">No hay slides. Agrega uno para personalizar tu carrusel.</div>
              )}
            </div>
          </div>
        )}

        {/* ===== SETTINGS TAB ===== */}
        {activeTab === "settings" && (
          <div className="space-y-6">
            <h3 className="text-lg font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>Configuración del Sitio</h3>
            <div className="bg-neutral-900/30 border border-neutral-800 rounded-2xl p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs text-neutral-500 uppercase tracking-wider mb-2">Teléfono</label>
                  <input data-testid="settings-phone" className={inputClass} value={siteSettings.phone || ""} onChange={(e) => setSiteSettings({ ...siteSettings, phone: e.target.value })} placeholder="(55) 4442-1964" />
                </div>
                <div>
                  <label className="block text-xs text-neutral-500 uppercase tracking-wider mb-2">WhatsApp (solo números)</label>
                  <div className="flex items-center gap-2">
                    <svg viewBox="0 0 24 24" className="w-5 h-5 fill-[#25D366] flex-shrink-0"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    <input data-testid="settings-whatsapp" className={inputClass} value={siteSettings.whatsapp || ""} onChange={(e) => setSiteSettings({ ...siteSettings, whatsapp: e.target.value })} placeholder="5544421964" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-neutral-500 uppercase tracking-wider mb-2">Correo electrónico</label>
                  <input data-testid="settings-email" className={inputClass} value={siteSettings.email || ""} onChange={(e) => setSiteSettings({ ...siteSettings, email: e.target.value })} placeholder="correo@empresa.com" />
                </div>
                <div>
                  <label className="block text-xs text-neutral-500 uppercase tracking-wider mb-2">Dirección</label>
                  <input data-testid="settings-address" className={inputClass} value={siteSettings.address || ""} onChange={(e) => setSiteSettings({ ...siteSettings, address: e.target.value })} placeholder="Ciudad de México, México" />
                </div>
              </div>

              <div className="border-t border-neutral-800 pt-5">
                <p className="text-xs text-neutral-500 uppercase tracking-wider mb-4">Redes Sociales</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <div>
                    <label className="block text-xs text-neutral-600 mb-2">Instagram</label>
                    <input data-testid="settings-instagram" className={inputClass} value={siteSettings.instagram || ""} onChange={(e) => setSiteSettings({ ...siteSettings, instagram: e.target.value })} placeholder="https://instagram.com/..." />
                  </div>
                  <div>
                    <label className="block text-xs text-neutral-600 mb-2">Facebook</label>
                    <input data-testid="settings-facebook" className={inputClass} value={siteSettings.facebook || ""} onChange={(e) => setSiteSettings({ ...siteSettings, facebook: e.target.value })} placeholder="https://facebook.com/..." />
                  </div>
                  <div>
                    <label className="block text-xs text-neutral-600 mb-2">Sitio Web</label>
                    <input data-testid="settings-website" className={inputClass} value={siteSettings.website || ""} onChange={(e) => setSiteSettings({ ...siteSettings, website: e.target.value })} placeholder="https://www.tuempresa.com" />
                  </div>
                </div>
              </div>

              <button data-testid="save-settings-btn" onClick={saveSettings}
                className="flex items-center gap-2 px-6 py-3 bg-white text-black text-sm font-medium rounded-full hover:bg-neutral-200 transition-colors">
                <Save size={14} /> Guardar configuración
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
